import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { ContextPane } from "@/components/ContextPane";
import { CommandPalette } from "@/components/CommandPalette";
import { HelpDialog } from "@/components/HelpDialog";
import {
  useSidebarOpen,
  useContextOpen,
  openCommandPalette,
  toggleSidebar,
  toggleContext,
  toggleHelpDialog,
  closeCommandPalette,
  type ContextTab,
} from "@/lib/uiState";
import { useShortcut, SHORTCUTS } from "@/lib/shortcuts";
import { cn } from "@/lib/cn";
import { useDesktopBridge } from "@/lib/desktop";
import { useAutoRefresh } from "@/lib/appVersion";
import { useTranslation } from "react-i18next";
import { sessionScope, useSession } from "@/lib/auth";
import { useGlobalActivity } from '@/lib/global-activity';

const MOBILE_BREAKPOINT = 768;
const COLLAPSED_KEY = "ailechat:sidebarCollapsed";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function readBool(key: string, def: boolean): boolean {
  if (typeof localStorage === "undefined") return def;
  const v = localStorage.getItem(key);
  if (v === null) return def;
  return v === "1";
}

function writeBool(key: string, val: boolean): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(key, val ? "1" : "0");
}

function useViewportIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

/**
 * Persisted "slim" sidebar mode (desktop-only). Independent from `sidebarOpen`,
 * which on desktop stays true and on mobile controls the drawer.
 */
function useSidebarCollapsed(): [boolean, (v?: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => readBool(COLLAPSED_KEY, false));
  const set = (v?: boolean) => {
    setCollapsed((prev) => {
      const next = typeof v === "boolean" ? v : !prev;
      writeBool(COLLAPSED_KEY, next);
      return next;
    });
  };
  return [collapsed, set];
}

/* -------------------------------------------------------------------------- */
/* Layout                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Top-level 3-pane shell:
 *
 *   ┌─────────────┬────────────────────────────────┬───────────────┐
 *   │ Sidebar     │ <Outlet/>                      │ ContextPane   │
 *   │ 256 / 64 px │ flex-1, min-w-0                │ 320 px        │
 *   └─────────────┴────────────────────────────────┴───────────────┘
 *
 * - Desktop (≥ 760 px): Sidebar always visible, can collapse to slim 64 px.
 * - Mobile  (< 760 px): Sidebar / ContextPane render as overlay drawers.
 * - Skip-link target = `<main id="main">` for screen-reader & keyboard users.
 * - State persisted: `ailechat:sidebarOpen`, `ailechat:contextOpen`,
 *   `ailechat:sidebarCollapsed` in localStorage.
 */
export function Layout(): JSX.Element {
  const session=useSession();
  return <WorkspaceLayout key={sessionScope(session)} />;
}

function WorkspaceLayout(): JSX.Element {
  useDesktopBridge();
  useGlobalActivity();
  useAutoRefresh();
  const { t } = useTranslation();
  const [sidebarOpen, setSidebar] = useSidebarOpen();
  const [contextTab, setContextTab] = useState<ContextTab>("members");
  const [contextOpen, setContext] = useContextOpen(setContextTab);
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const isMobile = useViewportIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPath = useRef(location.pathname);
  const allowContext = /^\/(?:rooms|channels|dm)\/[^/]+/.test(location.pathname);

  /* Auto-close mobile drawers on navigation. */
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    if (isMobile) {
      setSidebar(false);
      setContext(false);
    }
  }, [location.pathname, isMobile, setSidebar, setContext]);

  useEffect(() => {
    if (isMobile) { setSidebar(false); setContext(false); }
    else setContext(readBool("ailechat:contextOpen", true));
  }, [isMobile, setSidebar, setContext]);

  /* Desktop default: sidebar always present. Only flip the drawer flag on
     mobile, where its absence would hide content. */
  useEffect(() => {
    if (!isMobile && !sidebarOpen) {
      setSidebar(true);
    }
  }, [isMobile, sidebarOpen, setSidebar]);

  /* ---------- Global keyboard shortcuts ---------- */
  useShortcut(SHORTCUTS.search, () => openCommandPalette(), { allowInInput: true });
  useShortcut(SHORTCUTS.toggleSidebar, () => {
    if (isMobile) toggleSidebar();
    else setCollapsed();
  });
  useShortcut(SHORTCUTS.toggleContext, () => { if (allowContext) toggleContext(); });
  useShortcut(SHORTCUTS.helpDialog, () => toggleHelpDialog());
  useShortcut(SHORTCUTS.settings, () => navigate("/settings"));
  useShortcut(SHORTCUTS.newDm, () => navigate("/dm"));
  useShortcut(SHORTCUTS.myTodos, () => navigate("/tasks"));
  useShortcut(SHORTCUTS.searchFocus, () => openCommandPalette());
  useShortcut(
    SHORTCUTS.esc,
    () => {
      closeCommandPalette();
      toggleHelpDialog(false);
      if (isMobile) {
        setSidebar(false);
        setContext(false);
      }
    },
    { allowInInput: true },
  );

  const showSidebarOverlay = isMobile && sidebarOpen;
  const showContextOverlay = isMobile && contextOpen && allowContext;

  return (
    <div className="flex h-screen w-full bg-canvas overflow-hidden text-fg-primary">
      {/* Skip link — first DOM node so Tab from URL bar lands here. */}
      <a
        href="#main"
        className={cn(
          "sr-only focus:not-sr-only",
          "fixed top-2 left-2 z-[100] px-3 py-2 rounded-md bg-canvas text-brand-700",
          "border border-border-default shadow-md text-sm font-medium",
          "focus-visible:shadow-focus",
        )}
      >
        {t("a11y.skipToMain", { defaultValue: "メインコンテンツへスキップ" })}
      </a>

      {/* ---------- Desktop sidebar (in-flow column) ---------- */}
      {!isMobile && (
        <div
          className={cn(
            "shrink-0 h-full overflow-hidden transition-[width] duration-150 ease-out",
            collapsed ? "w-16" : "w-64",
          )}
        >
          <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed()} />
        </div>
      )}

      {/* ---------- Main column ---------- */}
      <main
        id="main"
        tabIndex={-1}
        className="flex-1 min-w-0 flex flex-col bg-canvas focus:outline-none"
      >
        <Outlet />
      </main>

      {/* ---------- Desktop context pane (in-flow column) ---------- */}
      {!isMobile && allowContext && (
        <div
          className={cn(
            "shrink-0 h-full overflow-hidden transition-[width] duration-150 ease-out",
            contextOpen ? "w-80" : "w-0",
          )}
          aria-hidden={!contextOpen}
        >
          {contextOpen ? <ContextPane tab={contextTab} onTabChange={setContextTab} /> : null}
        </div>
      )}

      {/* ---------- Mobile sidebar drawer ---------- */}
      {showSidebarOverlay ? (
        <div
          className="md:hidden fixed inset-0 z-40 bg-fg-primary/40"
          onClick={() => setSidebar(false)}
        >
          <div
            className="absolute inset-y-0 left-0 w-64 bg-surface-base shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t("nav.workspace")}
          >
            <Sidebar collapsed={false} onNavigate={() => setSidebar(false)} />
          </div>
        </div>
      ) : null}

      {/* ---------- Mobile context drawer ---------- */}
      {showContextOverlay ? (
        <div
          className="md:hidden fixed inset-0 z-40 bg-fg-primary/40"
          onClick={() => setContext(false)}
        >
          <div
            className="absolute inset-y-0 right-0 w-80 max-w-[85vw] bg-surface-base shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t("common.details")}
          >
            <ContextPane tab={contextTab} onTabChange={setContextTab} />
          </div>
        </div>
      ) : null}

      <CommandPalette />
      <HelpDialog />
    </div>
  );
}
